import { compact } from '../common.utils';
import { BarcodeScanningDevice } from '../../pace-barcode-scanner/pace-barcode-scanner.model';
import { PDF417_FORMAT } from './scan.const';

/**
 * Matches the scanResult against the provided pattern and processes the result.
 *
 * @param {string} scanResult - The scanned barcode result.
 * @param {string | undefined} pattern - The pattern to match against the scanResult. If undefined, the scanResult is returned as is.
 * @param {string} [format] - Optional barcode format, used to allow PDF417 partial extraction only.
 * @returns {string} - The processed result after matching and applying any replacements specified by the pattern.
 * @throws {Error} - Throws an error if the pattern does not match the scanResult.
 */
export function matchAndProcessBarcode(scanResult: string, pattern: string | undefined, format?: string): string {
    if (!pattern) {
        return scanResult;
    }
    const matchResult: string[] = compact(scanResult.match(pattern));
    if (matchResult?.length > 0 && matchResult[0] === scanResult) {
        // do any replacements that are given by the validation pattern, e.g. remove "20T" prefix
        if (matchResult.length > 1) {
            return matchResult[1];
        }
        return scanResult;
    }

    if (format?.toLowerCase() === PDF417_FORMAT) {
        if (matchResult?.length > 0) {
            return matchResult[0];
        }
    }
    throw scanResult;
}

/**
 * Checks if a given text matches a specified pattern.
 *
 * This function takes a text string and a pattern string (which can be undefined),
 * and returns a boolean indicating whether the text matches the pattern.
 * If the pattern is undefined, the function returns true.
 * If the pattern is a valid regular expression and the text matches it, the function returns true.
 * If the pattern is a valid regular expression and the text does not match it, the function returns false.
 * If the pattern is an invalid regular expression, the function catches the error and returns false.
 *
 * @param {string} text - The text to be checked against the pattern.
 * @param {string | undefined} pattern - The pattern to check the text against. Can be undefined.
 * @param {string} [format] - Optional barcode format, used to allow PDF417 partial extraction only.
 * @returns {boolean} - true, if the text matches the pattern, or if the pattern is undefined.
 */
export function matchesPattern(text: string, pattern: string | undefined, format?: string): boolean {
    try {
        matchAndProcessBarcode(text, pattern, format);
        return true;
    } catch {
        return false;
    }
}

function scoreCamera(label: string, facingMode?: string): number {
    const l = label.toLowerCase();
    let score = 0;

    if (facingMode === 'environment') {
        score += 150;
    } else if (facingMode === 'user') {
        score -= 120;
    }

    const scores: Array<[RegExp, number]> = [
        [/\b(back|rear)\b/, 100],
        [/\b(front|selfie)\b/, -100],
        [/\b(wide)\b/, 30],
        [/\bultra\s*wide\b/, -10],
        [/\btele(photo)?\b/, 20],
        [/\bmacro\b/, -60],
        [/\b(depth|tof|bokeh|ai depth)\b/, -80],
        [/\b(main|primary|default)\b/, 25],
        [/\b(dual|triple)\b.*\bwide\b/, 30],
        [/\b(back\s+camera)\b/, 10],
        [/\b(back\s+dual\s+wide\s+camera)\b/, 15],
    ];

    for (const [regex, s] of scores) {
        if (regex.test(l)) {
            score += s;
        }
    }
    return score;
}

export async function getDevicesInfo(device: MediaDeviceInfo): Promise<BarcodeScanningDevice | undefined> {
    const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: device.deviceId } });
    const track = mediaStream?.getVideoTracks()?.[0];
    if (track) {
        const settings = track.getSettings();
        mediaStream?.getTracks()?.forEach((t) => t?.stop());
        return { device, settings, score: scoreCamera(device.label, settings.facingMode) };
    }
    return undefined;
}

export function selectInitialDevice(
    devices: BarcodeScanningDevice[],
    lastCam: string | null | undefined,
): string | undefined {
    const lastDevice = devices.find((device) => device.device.deviceId === lastCam)?.device?.deviceId;
    if (lastCam && lastDevice) {
        return lastDevice;
    }

    if (devices.length < 1) {
        return undefined;
    }

    return devices.reduce((prev, cur) => (prev && prev.score > cur.score ? prev : cur)).device.deviceId;
}
